import { noddiAsync, URLKeys } from 'noddi-async';

import { t, Trans } from '@lingui/macro';
import { useAuthContext } from 'noddi-provider';
import { NoddiButton } from 'noddi-ui-common';
import { NoddiBasicCard } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiErrorMessageWithTrans } from '../../../components/ApiErrorMessageWithTrans';
import { CustomerSMSLogin } from '../../../components/CustomerSMSLogin';
import ContentWrapperWithQuery from '../../../components/Layouts/ContentWrapperWithQuery';
import { TransparentBottomBar } from '../../../components/Layouts/TransparentBottomBar';
import { TireOfferNextInstructions } from '../../../components/TireOffer/TireOfferNextInstructions';
import { TireOfferPaymentSummary } from '../../../components/TireOffer/TireOfferPaymentSummary';
import { useTireOfferStore } from '../../../stores/tireOfferStore';
import CommentBox from '../../BookingFlow/Steps/Summary/CommentBox';

export const TireOfferSummary = () => {
  const { slug } = useParams() as { slug: string };
  const { selectedQuoteItem } = useTireOfferStore();
  const { isLoggedIn } = useAuthContext();
  const [userComment, setUserComment] = useState('');

  const navigate = useNavigate();

  const query = noddiAsync.useGet({
    type: URLKeys.getCarTireQuoteBySlug,
    input: { slug },
    queryConfig: { staleTime: Infinity }
  });

  const {
    mutateAsync: confirmTireQuoteOffer,
    isPending: isConfirmTireQuoteOfferPending,
    error: confirmTireQuoteOfferError
  } = noddiAsync.usePost({
    type: URLKeys.postConfirmTireQuoteOffer,
    queryConfig: {
      onSuccess: async (data) => {
        navigate(
          customerRoutes.tireOfferConfirmation.getPath({
            sessionId: data.data.paymentSessionProfile.sessionId
          })
        );
      }
    }
  });

  if (!selectedQuoteItem) {
    return null;
  }

  return (
    <ContentWrapperWithQuery title={t`Ready to confirm your order?`} query={query}>
      {({ data }) => (
        <div className='flex flex-col gap-4'>
          <NoddiBasicCard className='mt-5 flex flex-col gap-2'>
            <p className='text-5'>
              <Trans>Your order</Trans>
            </p>
            <TireOfferPaymentSummary
              quoteItemTires={selectedQuoteItem.quoteItemTires}
              genericItems={data.genericItems}
            />
          </NoddiBasicCard>
          <TireOfferNextInstructions />

          {isLoggedIn ? (
            <div>
              <CommentBox
                comment={userComment}
                handleChange={setUserComment}
                title={t`Anything more we should know?`}
                placeholder={t`Optional note if any extra information is needed...`}
              />

              <TransparentBottomBar>
                {confirmTireQuoteOfferError && (
                  <div className='mb-4'>
                    <ApiErrorMessageWithTrans error={confirmTireQuoteOfferError} />
                  </div>
                )}
                <div className='md:flex md:justify-end'>
                  <NoddiButton
                    variant='success'
                    className='w-full md:w-fit'
                    loading={isConfirmTireQuoteOfferPending}
                    onPress={async () =>
                      await confirmTireQuoteOffer({
                        quoteItemId: selectedQuoteItem.id,
                        quoteItemTires: selectedQuoteItem.quoteItemTires.map((tire) => ({
                          quoteItemTireId: tire.id,
                          quantity: tire.quantitySelected ?? 0
                        })),
                        comments: userComment
                      })
                    }
                  >
                    <Trans>Confirm and go to payment</Trans>
                  </NoddiButton>
                </div>
              </TransparentBottomBar>
            </div>
          ) : (
            <div className='text-darkPurple'>
              <p className='mb-2 font-bold'>
                <Trans>You need to log in in order to confirm the order</Trans>
              </p>
              <CustomerSMSLogin />
            </div>
          )}
        </div>
      )}
    </ContentWrapperWithQuery>
  );
};
